import type { AxiosError } from 'axios';
import clsx from 'clsx';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useResetAtom } from 'jotai/utils';
import { isOnPremises } from 'libs/env';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { FormValidationError } from 'types/cloud_api_error_type';
import { isValidFormValidationError } from 'types/cloud_api_error_type';
import { Button } from '@/components/atoms/Button';
import type { SignInRequest } from 'apis/sign_in';
import { postSignIn } from 'apis/sign_in';
import type { SignateNotificationsUnsignedInResponse } from 'apis/signate_notifications/un_signed_in';
import { getSignateNotificationsUnSignedIn } from 'apis/signate_notifications/un_signed_in';
import { passwordResetRequestPage } from 'pages/password/pages';
import { passwordResetPage } from 'pages/password/reset/pages';
import { currentOrganizationUserAtom, currentUserAtom } from 'utils/atoms';
import {
  fieldState,
  getFirstErrorField,
  getFieldValidationError,
  hasHttpErrorMessage,
  httpErrorHandler,
  removeFieldValidationError,
} from 'utils/httpError';
import { emailValidator } from 'utils/validators';
import layoutMain from './_styles/layout/main.module.scss';
import objectComponentButton from './_styles/object/component/button.module.scss';
import objectComponentCard from './_styles/object/component/card.module.scss';
import objectComponentForm from './_styles/object/component/form.module.scss';
import pageRegistration from './_styles/page/registration.module.scss';
import { OutOfServiceAnnouncement } from './OutOfServiceAnnouncement';
import { SignateNotificationCarousel } from './SignateNotificationCarousel';
import { checkUsersMe, checkOrganizationUsersMe } from './signIn';
import { SignInFooter } from './SignInFooter';
import { SignInHeader } from './SignInHeader';
import { SignInInput } from './SignInInput';
dayjs.extend(isBetween);

export const getServerSideProps = (
  context: GetServerSidePropsContext
): GetServerSidePropsResult<{ redirectUrl: string }> => {
  return {
    props: {
      redirectUrl: String(context.query.redirect_url ?? ''),
    },
  };
};

const SignInPage = ({ redirectUrl }: { redirectUrl: string }) => {
  const router = useRouter();
  const resetCurrentUser = useResetAtom(currentUserAtom);
  const resetCurrentOrganizationUser = useResetAtom(
    currentOrganizationUserAtom
  );
  const {
    register,
    getValues,
    trigger,
    setFocus,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInRequest>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUnregister: false,
    criteriaMode: 'all',
    shouldFocusError: true,
  });
  const [formValidationError, setFormValidationError] =
    useState<FormValidationError>({
      errorType: 'error',
      message: '',
      details: [],
    });
  const [isFormErrorOccurred, setIsFormErrorOccurred] =
    useState<boolean>(false);
  const [signateNotifications, setSignateNotifications] =
    useState<SignateNotificationsUnsignedInResponse>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const apiCalledRef = useRef(false);
  const objectCButtonClass = objectComponentButton['c-button'];
  const objectCButtonDisabledClass = objectComponentButton['c-button-disabled'];
  const pageCButtonClass = pageRegistration['c-button'];
  const objectCRegistrationButtonClass =
    objectComponentButton['c-registration-button'];
  const pageCRegistrationButtonClass =
    pageRegistration['c-registration-button'];

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onSubmit: SubmitHandler<SignInRequest> = (request) => {
    trigger().then((isValid) => {
      if (!isValid) return;
    });

    setIsSubmitting(true);
    postSignIn(request)
      .then((response) => {
        if (response.status === 200) {
          if (!response.data.success && response.data.resetPasswordToken) {
            router?.push({
              pathname: `${passwordResetPage.path}/${response.data.resetPasswordToken}`,
              query: { forceChange: true },
            });
          } else {
            checkOrganizationUsersMe(router, redirectUrl);
          }
        }
      })
      .catch((error: AxiosError) => {
        setIsSubmitting(false);
        setIsFormErrorOccurred(hasHttpErrorMessage(error));
        if (error.response && isValidFormValidationError(error.response.data)) {
          setFormValidationError(error.response.data as FormValidationError);
          const errorField: keyof SignInRequest | undefined =
            getFirstErrorField(error.response.data);
          if (errorField) setFocus(errorField);
        }
        httpErrorHandler(router, error, {
          noRedirection: error.response?.status === 401,
        });
      });
  };

  useEffect(() => {
    if (!router?.isReady) return;

    // See: https://github.com/reactwg/react-18/discussions/18#discussion-3385714
    if (apiCalledRef.current === false) {
      apiCalledRef.current = true;
      if (!checkUsersMe(router, redirectUrl)) {
        // NOTE :セッション切れ／なしのため、 atom クリア
        resetCurrentUser();
        resetCurrentOrganizationUser();
      }
      getSignateNotificationsUnSignedIn({ targetPage: 'sign_in' })
        .then((response) => {
          setSignateNotifications(response.data);
        })
        .catch((error: AxiosError) => {
          httpErrorHandler(router, error, { noRedirection: true });
        });
    }
  }, [router, redirectUrl, resetCurrentUser, resetCurrentOrganizationUser]);

  // バックエンドからリダイレクト時に一度だけエラー表示
  useEffect(() => {
    if (!router?.isReady) return;

    const error = router.query.error as string;
    if (error && error != '') {
      toast.error(error);
      const { pathname, query } = router;
      delete query.error;
      router.replace({ pathname, query: query }, undefined, { shallow: true });
    }
  });

  const clearFormAndFieldError = (field: keyof SignInRequest) => {
    setIsFormErrorOccurred(false);
    clearErrors(field);
    removeFieldValidationError(field, formValidationError);
  };

  const pRegistrationWrapperClass = clsx(
    objectComponentCard['c-card'],
    pageRegistration['p-registration-wrapper']
  );

  return (
    <div className={pageRegistration['p-registration']}>
      <SignInHeader />
      <div
        className={clsx(
          layoutMain['l-main'],
          pageRegistration['l-main'],
          layoutMain['l-container']
        )}
      >
        <div
          id="bizusers-sessions-new"
          className={pageRegistration['p-registration-step']}
        >
          <OutOfServiceAnnouncement />
          <div
            className={pRegistrationWrapperClass}
            style={
              signateNotifications && signateNotifications?.totalCount > 0
                ? { marginBottom: '30px' }
                : {}
            }
          >
            <h3>SIGNATE Cloud にサインインする</h3>
            <form onSubmit={handleSubmit(onSubmit)}>
              <SignInInput
                id="biz_user_email"
                type="email"
                autoComplete="email"
                placeholder="メールアドレスを入力してください"
                label="メールアドレス"
                classNames={{ root: 'mg-t-30' }}
                state={fieldState(
                  isFormErrorOccurred,
                  'email',
                  formValidationError,
                  errors.email
                )}
                fieldError={errors.email}
                validationError={getFieldValidationError(
                  'email',
                  formValidationError
                )}
                required
                {...register('email', {
                  ...emailValidator,
                  onBlur: () => {
                    if (getValues('email') !== null)
                      clearFormAndFieldError('email');
                  },
                })}
              />
              <SignInInput
                id="biz_user_password"
                type="password"
                autoComplete="new-password"
                placeholder="パスワードを入力してください"
                label="パスワード"
                state={fieldState(
                  isFormErrorOccurred,
                  'password',
                  formValidationError,
                  errors.password
                )}
                fieldError={errors.password}
                validationError={getFieldValidationError(
                  'password',
                  formValidationError
                )}
                required
                {...register('password', {
                  // 文字数の最低が6=>8に変更になったのでフロントで認証用のパスワードに
                  // バリデーションかけるとそもそも次に進めなくなるケースがあるのでpasswordValidatorは使用しない
                  required: '入力してください',
                  onBlur: () => {
                    if (getValues('password') !== null)
                      clearFormAndFieldError('password');
                  },
                })}
              />
              <label className={objectComponentForm['c-checkbox-label']}>
                <input
                  type="checkbox"
                  {...register('rememberMe')}
                  className={objectComponentForm['c-checkbox']}
                />
                <span className={objectComponentForm['c-custom-checkbox']} />
                サインイン状態を保持する
              </label>
              <Button
                className={clsx(
                  objectCButtonClass,
                  pageCButtonClass,
                  objectCRegistrationButtonClass,
                  pageCRegistrationButtonClass,
                  isSubmitting && objectCButtonDisabledClass,
                  'mg-t-30'
                )}
                type="submit"
                disabled={isSubmitting}
              >
                <i className="fa fa-sign-in-alt mg-r-5"></i>
                サインイン
              </Button>
            </form>
            {!isOnPremises() && (
              <ul className={clsx(pageRegistration['p-list-link'])}>
                <li className="forgot-password">
                  <Link
                    href={passwordResetRequestPage.path}
                    data-testid="password-reset-request-link"
                  >
                    パスワードを忘れた方
                  </Link>
                </li>
                <li className="contact">
                  <Link
                    href={`${process.env.NEXT_PUBLIC_SIGNATE_CLOUD_SUPPORT_ARTICLES_URL}/157000219850`}
                    target="_blank"
                  >
                    サインインでお困りの方はこちら
                  </Link>
                </li>
              </ul>
            )}
            <Button
              type="button"
              onClick={() =>
                router?.push({
                  pathname: 'sign_in/azure_saml',
                  query: router?.query.redirect_url
                    ? { redirect_url: router?.query.redirect_url }
                    : {},
                })
              }
              className={clsx(
                objectCButtonClass,
                pageCButtonClass,
                objectCRegistrationButtonClass,
                pageCRegistrationButtonClass,
                'mg-t-20',
                'azure-ad-btn'
              )}
              style={{ textTransform: 'none' }}
            >
              <i className="fa fa-arrow-right mg-r-5"></i>
              Azure AD
              <span className="mg-l-5">で</span>
              サインイン
            </Button>
            <Button
              type="button"
              onClick={() =>
                router?.push({
                  pathname: 'sign_in/okta_saml',
                  query: router?.query.redirect_url
                    ? { redirect_url: router?.query.redirect_url }
                    : {},
                })
              }
              className={clsx(
                objectCButtonClass,
                pageCButtonClass,
                objectCRegistrationButtonClass,
                pageCRegistrationButtonClass,
                'mg-t-20',
                'okta-btn'
              )}
              style={{ textTransform: 'none' }}
            >
              <i className="fa fa-arrow-right mg-r-5"></i>
              Okta
              <span className="mg-l-5">で</span>
              サインイン
            </Button>
          </div>
          <SignateNotificationCarousel
            signateNotificationsResource={signateNotifications}
          />
        </div>
      </div>

      <SignInFooter />
      <style>
        {`
          form {
            margin: 15px 0;
          }

          .forgot-password {
            list-style: none;
          }

          .contact {
            margin-top: 5px;
          }

          .azure-ad-btn {
            background-color: #1b84e7;
          }

          .okta-btn {
            background-color: #1b84e7;
          }

          .mg-l-5 {
            margin-left: 5px;
          }

          .mg-t-15 {
            margin-top: 15px;
          }

          .mg-t-20 {
            margin-top: 20px;
          }

          .mg-t-30 {
            margin-top: 30px;
          }

          .mg-r-5 {
            margin-right: 5px;
          }
        `}
      </style>
    </div>
  );
};

export default SignInPage;
